import React from "react";
import { MapStateToPropsParam } from "react-redux";
import { RouteComponentProps } from "react-router";

import { StrollerAllReviewsGallery } from "@/mk2/apps/strollers/containers/StrollerAllReviewsGallery/StrollerAllReviewsGallery";
import { strollerAllReviewsGalleryFetchTrigger } from "@/mk2/apps/strollers/containers/StrollerAllReviewsGallery/StrollerAllReviewsGallery.actions";
import { strollersAllReviewsPhotosGalleriesReducer } from "@/mk2/apps/strollers/containers/StrollerAllReviewsGallery/StrollerAllReviewsGallery.reducers";
import strollersAllReviewsPhotosSaga from "@/mk2/apps/strollers/containers/StrollerAllReviewsGallery/StrollerAllReviewsGallery.sagas";
import { getStrollerAllReviewsGalleryPageState } from "@/mk2/apps/strollers/containers/StrollerAllReviewsGallery/StrollerAllReviewsGallery.selectors";
import { strollersReviewsUrl } from "@/mk2/apps/strollers/urls";
import { AppName } from "@/mk2/containers/Application/Application";
import { cacheLast } from "@/mk2/helpers/cache";
import { MapDispatchToPropsObject } from "@/mk2/helpers/types";
import { url } from "@/mk2/helpers/urls";
import Page, { HalfpageProps, RightContentSide } from "@/mk2/pages/Page";
import { AppState } from "@/mk2/reducers";
import { LoadingState } from "@/mk2/reducers/loadingState";
import {
    StrollerEntity,
    StrollerSchema,
    StrollerUserReviewEntity,
    StrollerUserReviewPhotoEntity,
    StrollerUserReviewPhotoSchema,
    StrollerUserReviewSchema,
} from "@/mk2/schemas";
import { getDenormalizedEntities, getDenormalizedEntity } from "@/mk2/selectors";
import { interpolate } from "@/mk2/services/i18n";
import { Store } from "@/mk2/store/configureStore";
import {
    SEO_TITLE_STROLLER_REVIEWS_stroller,
    STROLLER_SEO_GENERAL_DESC,
} from "@/mk/autogenerated/translation";

export interface OwnProps {}

export interface StateProps {
    strollerSlug: string;
    loadingState: LoadingState;
    stroller: StrollerEntity;
    strollerReviews: StrollerUserReviewEntity[];
    strollerReviewsPhotos: StrollerUserReviewPhotoEntity[];
}

export interface DispatchProps {
    onLoad(slug: string);
}

export type RouteProps = RouteComponentProps<{
    slug: string;
}>;

export type Props = OwnProps & StateProps & DispatchProps & RouteProps;

export default class StrollerAllReviewsGalleryPage extends Page<
    OwnProps & RouteProps,
    StateProps,
    DispatchProps
> {
    private handleOnExit = cacheLast();

    public mapStateToProps(): MapStateToPropsParam<StateProps, OwnProps & RouteProps, AppState> {
        return (state: AppState, ownProps: OwnProps & RouteProps): StateProps => {
            const slug = ownProps.match.params.slug;

            const photosState = getStrollerAllReviewsGalleryPageState(state, slug);
            const stroller = getDenormalizedEntity<StrollerEntity>(
                state,
                StrollerSchema,
                photosState.strollerId,
            );
            const strollerReviews = getDenormalizedEntities<StrollerUserReviewEntity>(
                state,
                StrollerUserReviewSchema,
                photosState.strollerReviewIds,
            );
            const strollerReviewsPhotos = getDenormalizedEntities<StrollerUserReviewPhotoEntity>(
                state,
                StrollerUserReviewPhotoSchema,
                photosState.strollerReviewsPhotoIds,
            );

            return {
                strollerSlug: slug,
                loadingState: photosState.loadingState,
                stroller,
                strollerReviews,
                strollerReviewsPhotos,
            };
        };
    }

    public mapDispatchToProps(): MapDispatchToPropsObject<DispatchProps> {
        return {
            onLoad: strollerAllReviewsGalleryFetchTrigger,
        };
    }

    public getMetaTitle(props: Props): string {
        const { stroller } = props;
        return stroller
            ? interpolate(SEO_TITLE_STROLLER_REVIEWS_stroller, { stroller: stroller.name })
            : null;
    }

    public getMetaDescription(props: Props): string {
        const { stroller } = props;
        if (stroller) {
            return `${stroller.name} - ${STROLLER_SEO_GENERAL_DESC}`;
        }
    }

    public render(props: Props): false | JSX.Element {
        return <StrollerAllReviewsGallery {...props} />;
    }

    public getRightContentSide(app: AppName, props: Props): RightContentSide {
        return RightContentSide.RightSide;
    }

    public hasMobileLogo(props: Props): boolean {
        return true;
    }

    public hasHeaderBackButton(props: Props): boolean {
        return true;
    }

    public getDefaultBackPath(props: Props): string {
        const { strollerSlug } = props;
        return url(strollersReviewsUrl, { slug: strollerSlug });
    }

    public getHalfpageProps(props: Props): HalfpageProps {
        return {
            zone: "Strollers Index - Halfpage Sticky (Desktop)",
        };
    }

    public registerInStore(store: Store<AppState>) {
        store.injectReducer(
            "containers.strollers.strollersAllReviewsGalleries",
            strollersAllReviewsPhotosGalleriesReducer,
        );
        store.injectSaga(
            "containers.strollers.strollersAllReviewsGalleries",
            strollersAllReviewsPhotosSaga,
        );
    }
}
